import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, CircularProgress, Divider, Stack } from "@mui/material";

import { useDisplayField } from "../../../hooks/useDisplayField";
import { useFetchTaskDetails } from "../hooks/useFetchTaskDetails";
import { useAddCheckList, useEditCheckList } from "../../stageGates/mutation";
import { usePhase } from "../../projectOverview/hooks/usePhase";
import { useCapabilities } from "../../../hooks/useCapabilities";

import { DataLayout, FieldControl, FileUpload } from "../../../shared/components";
import { useGetJeopardyStakeholders } from "../../../shared/hooks";
import { FileUploadRef } from "../../../shared/components/FileUpload";
import { AutocompleteOption, ControlledAutocomplete } from "../../../shared/components/ControlledAutocomplete";
import { Option } from "../../../shared/components/FieldControl";
import { filterCommercialCategory } from "../../../shared/utils/filterCommercialCategory";
import { useSearchProjects } from "../../projects/mutations";

export type TaskObjectType =
    | "task"
    | "recurring_task"
    | "stage_gate"
    | "checklist"
    | "activity"
    | "risk"
    | "queries"
    | "meeting"
    | "material_request"
    | "resource_request";

export const RaiseTask: React.FC<{ projectId?: string | undefined, isEdit: boolean, taskId: string | null; onClose: () => void; refetch: () => void; object_id?: string | null; object_type?: TaskObjectType; enabledFields?: string[]; disableFields?: string[]; meetingProjects?: Option[], meetingProgrammes?: Option[] }> = ({ projectId, isEdit, taskId, onClose, refetch, object_id, object_type, enabledFields, disableFields, meetingProjects, meetingProgrammes }) => {

    const addCheckList = useAddCheckList();
    const editCheckList = useEditCheckList();

    const { removeCache } = useFetchTaskDetails({ taskId: taskId || '' });
    const { isCommercialPerson, isAdminPerson } = useCapabilities();

    const searchProjects = useSearchProjects();

    const fileUploadRef = useRef<FileUploadRef | null>(null);

    const { getFilterOptions } = useDisplayField([]);
    const { taskDetails, isPending } = useFetchTaskDetails({ taskId: taskId || '' });

    const [task, setTask] = useState<{
        type: 'Project' | 'Portfolio' | 'Programme',
        project: null | AutocompleteOption,
        portfolio: null | AutocompleteOption,
        programme: null | AutocompleteOption,
        title: null | string,
        description: null | string,
        object_type : null | string,
        status: null | string,
        priority: null | string,
        assigned_to: null | number,
        target_date: null | string,
        phase: null | string,
        actionCategory: null | string,
        tags: string[] | [],
    }>({
        type: 'Project',
        title: null,
        description: null,
        object_type: null,
        status: null,
        priority: null,
        assigned_to: null,
        target_date: null,
        project: null,
        portfolio: null,
        programme: null,
        phase: null,
        actionCategory: null,
        tags: [],
    });

    const [taskErrors, setTaskErrors] = useState<{
        type: null | string;
        title: null | string;
        description: null | string;
        status: null | string;
        priority: null | string;
        assigned_to: null | string;
        target_date: null | string;
        project: null | string;
        portfolio: null | string;
        programme: null | string;
        phase: null | string;
        actionCategory: null | string;
    }>({
        type: null,
        title: null,
        description: null,
        status: null,
        priority: null,
        assigned_to: null,
        target_date: null,
        project: null,
        portfolio: null,
        programme: null,
        phase: null,
        actionCategory: null,
    });

    const validateFields = () => {
        const errors: any = {};
        if (!task.type) {
          errors.type = "Type is required";
        }
        if (!task.title) {
          errors.title = "Title is required";
        }
        if (!task.description) {
          errors.description = "Description is required";
        }
        if (!task.status) {
          errors.status = "Status is required";
        }
        if (!task.priority) {
          errors.priority = "Priority is required";
        }
        if (!task.assigned_to) {
          errors.assigned_to = "Assigned to is required";
        }
        if (!task.target_date) {
          errors.target_date = "Target date is required";
        }
        if (task.type === "Project" && !task.project) {
          errors.project = "Project is required";
        }
        if (task.type === "Portfolio" && !task.portfolio) {
          errors.portfolio = "Portfolio is required";
        }
        if (task.type === "Programme" && !task.programme?.id) {
          errors.programme = "Programme is required";
        }
        if (task.type === "Project" && !task.phase) {
          errors.phase = "Phase is required";
        }
        if (!task.actionCategory) {
          errors.actionCategory = "Action Category is required";
        }
        setTaskErrors(errors);
        return Object.keys(errors).length === 0;
    };



    const payload = () => {
        if (task.type === "Project" && task.project?.id) {
            return { project_id: task.project.id };
        } else if (task.type === "Programme" && task.programme?.id) {
            return { programme_id: task.programme.id };
        } else if (task.type === "Portfolio" && task.portfolio?.id) {
            return { portfolio_id: task.portfolio.id };
        }
        return {};
    };

    const { data: stakeholders, isLoading: isLoadingUsers, refetch: refetchStakeHolders } = useGetJeopardyStakeholders({ params: payload() });

    const userOptions = useMemo(() => {
        return stakeholders?.data?.flatMap((role: any) =>
            role?.users?.map((user: any) => ({
                id: user.user_id,
                name: user.user_name,
            }))
        ) || [];
    }, [stakeholders]);

    useEffect(() => {
        const currentProjectId = (task.type === "Project" ? task.project?.id : task.type === "Programme" ? task.programme?.id : task.type === "Portfolio" ? task.portfolio?.id : undefined);
        if (currentProjectId) {
            refetchStakeHolders();
        }
    }, [task.type, task.project?.id, task.programme?.id, task.portfolio?.id, refetchStakeHolders]);

    const onChange = (key: string, value: string | AutocompleteOption | AutocompleteOption[] | null) => {
        setTask((prevTask) => ({
            ...prevTask,
            [key]: value
        }));
        setTaskErrors((prevErrors) => ({
            ...prevErrors,
            [key]: null
        }));
    };


    const params = () => {
        const payload: any = {
            task_name: task.title,
            user_id: task.assigned_to,
            object_type: object_type || 'task',
            is_evidence_required: false,
            is_required: false,
            description: task.description,
            tags: task.tags,
            priority: task.priority,
            status: task.status,
            target_date: task.target_date,
            type: task.type,
            category: task.actionCategory,
        };
        if (task.type === 'Project' && task.project) {
            payload.project_id = task.project.id || task.project;
        };
        if (task.type === 'Portfolio' && task.portfolio) {
            payload.portfolio_id = task.portfolio.id || task.portfolio;
        };
        if (task.type === 'Programme' && task.programme) {
            payload.programme_id = task.programme.id || task.programme;
        };
        if (task.phase) {
            payload.project_tree_id = task.phase;
        };
        if (object_id) {
            payload.object_id = Number(object_id);
        };
        return payload;
    };

    const onSave = async () => {
        if (!validateFields()) return;
        if (isEdit && taskId) {
            await editCheckList.mutateAsync({
                payload: params(),
                checkListID: taskId
            });
            refetch(); // Refetch data after task update
            if (fileUploadRef.current && taskId) {
                fileUploadRef.current.setId(taskId);
                setTimeout(() => {
                    fileUploadRef.current?.uploadAllFiles();
                }, 0);
            } else {
                // Close drawer if no files to upload
                onClose();
            }
        } else {
            const response = await addCheckList.mutateAsync({
                payload: params()
            });
            refetch(); // Refetch data after task creation
            if (fileUploadRef.current && response.data?.task_id) {
                fileUploadRef.current.setId(response.data?.task_id);
                setTimeout(() => {
                    fileUploadRef.current?.uploadAllFiles();
                }, 0);
            } else {
                // Close drawer if no files to upload
                onClose();
            }
        };
    };

    useEffect(() => {
        if (isEdit && taskId) {
            if (taskDetails) {
                setTask((prevTask) => ({
                    ...prevTask,
                    title: taskDetails.task_name,
                    description: taskDetails.description,
                    tags: taskDetails.tags,
                    status: taskDetails.status,
                    priority: taskDetails.priority,
                    object_type: taskDetails.object_type,
                    assigned_to: taskDetails.user_id,
                    target_date: taskDetails.target_date,
                    type: taskDetails.type || prevTask.type,
                    project: taskDetails.project_id ? { id: taskDetails.project_id, label: taskDetails.project_title } : prevTask.project,
                    portfolio: taskDetails.portfolio_id ? { id: taskDetails.portfolio_id, label: taskDetails.portfolio_name } : prevTask.portfolio,
                    programme: taskDetails.programme_id ? { id: taskDetails.programme_id, label: taskDetails.programme_name } : prevTask.programme,
                    phase: taskDetails.project_tree_id,
                    actionCategory: taskDetails.category,
                }));
            }
        }
    }, [isEdit, taskId, taskDetails]);

    const activeProjectId = useMemo(() => {
        return task.project?.id;
    }, [task.project?.id]);
    
    const phaseData = usePhase(activeProjectId ? String(activeProjectId) : undefined);

    const fetchProjectDetails = async () => {
        const params: Record<string, any> = {
            project_id: projectId
        };
        const response = await searchProjects.mutateAsync({ params });
        setTask((prevTask) => ({
            ...prevTask,
            project: {
                id: response.data[0].id,
                label: response.data[0].title
            }
        }));
    };

    useEffect(() => {
        if (projectId)
            fetchProjectDetails();
    }, [projectId]);

    useEffect(() => {

    }, []);

    if (isPending && isEdit) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        )
    };

    return (
        <Box display={'flex'} flexDirection={'column'} gap={2} height={'100%'} overflow={'hidden'}>
            <Box flex={1} sx={{ overflowY: 'auto' }} p={2}>
                <Box display={'flex'} flexDirection={'column'} gap={2}>

                    {
                        !disableFields?.includes('type') &&
                        <FieldControl
                            keyName={'type'}
                            label={"Type"}
                            variant={"select"}
                            options={[
                                { label: 'Project', value: 'Project' },
                                { label: 'Portfolio', value: 'Portfolio' },
                                { label: 'Programme', value: 'Programme' },
                            ]}
                            value={task.type}
                            onChange={onChange}
                            required
                            error={!!taskErrors.type}
                            helperText={taskErrors.type}
                        />
                    }

                    {
                        !disableFields?.includes('type') && task.type === 'Project' &&
                        <ControlledAutocomplete
                            type="PROJECT"
                            value={task.project}
                            onSelect={(val) => onChange('project', val)}
                            label="Search project"
                            placeholder="Search project"
                            required
                            error={!!taskErrors.project}
                            helperText={taskErrors.project}
                        />
                    }

                    {
                        !disableFields?.includes('type') && task.type === 'Project' &&
                        <FieldControl
                            keyName={'phase'}
                            label={"Select Project Phase"}
                            variant={"select"}
                            value={task.phase}
                            options={
                                phaseData.data?.data?.map((value) => {
                                    return {
                                        value: value.id, label: value.name
                                    }
                                })
                            }
                            onChange={onChange}
                            loading={phaseData.isPending}
                            required
                            error={!!taskErrors.phase}
                            helperText={taskErrors.phase}
                        />
                    }

                    {
                        !disableFields?.includes('type') && task.type === 'Portfolio' &&
                        <ControlledAutocomplete
                            type="PORTFOLIO"
                            value={task.portfolio}
                            onSelect={(val) => onChange('portfolio', val)}
                            label="Search portfolio"
                            placeholder="Search portfolio"
                            required
                            error={!!taskErrors.portfolio}
                            helperText={taskErrors.portfolio}
                        />
                    }

                    {
                        !disableFields?.includes('type') && task.type === 'Programme' &&
                        <ControlledAutocomplete
                            type="PROGRAMME"
                            value={task.programme}
                            onSelect={(val) => onChange('programme', val)}
                            label="Search programme"
                            placeholder="Search programme"
                            required
                            error={!!taskErrors.programme}
                            helperText={taskErrors.programme}
                        />
                    }

                    {
                        !disableFields?.includes('type') &&
                        <Divider />
                    }

                    {
                        enabledFields && enabledFields.includes('meeting_project') &&
                        <FieldControl
                            keyName={"project"}
                            label={"Project"}
                            variant={"select"}
                            value={task.project?.id}
                            options={meetingProjects || []}
                            onChange={(_key: string, value: any) => {
                                const selectedOption = meetingProjects?.find((option) => option.value === value) || null;
                                setTask((prevTask) => ({
                                    ...prevTask,
                                    type: 'Project',
                                    project: selectedOption ? { id: selectedOption.value, label: String(selectedOption.label) } : null
                                }));
                            }}
                            required
                            error={!!taskErrors.project}
                            helperText={taskErrors.project}
                        />
                    }

                    {
                        enabledFields && enabledFields.includes('meeting_project') &&
                        <FieldControl
                            keyName={'phase'}
                            label={"Select Project Phase"}
                            variant={"select"}
                            value={task.phase}
                            options={
                                phaseData.data?.data?.map((value) => {
                                    return {
                                        value: value.id, label: value.name
                                    }
                                })
                            }
                            onChange={onChange}
                            loading={phaseData.isPending}
                            required
                            error={!!taskErrors.phase}
                            helperText={taskErrors.phase}
                        />
                    }

                    {
                        enabledFields && enabledFields.includes('meeting_programme') &&
                        <FieldControl
                            keyName={"programme"}
                            label={"Programme"}
                            variant={"select"}
                            value={task.programme}
                            options={meetingProgrammes || []}
                            onChange={(_key: string, value: any) => {
                                setTask((prevTask) => ({
                                    ...prevTask,
                                    type: 'Programme',
                                    programme: value,
                                }));
                            }}
                            required
                            error={!!taskErrors.programme}
                            helperText={taskErrors.programme}
                        />
                    }

                    <FieldControl
                        keyName={"title"}
                        label={"Task Title"}
                        variant={"input"}
                        value={task.title}
                        onChange={onChange}
                        required
                        error={!!taskErrors.title}
                        helperText={taskErrors.title}
                    />

                    <FieldControl
                        keyName={"description"}
                        label={"Description"}
                        variant={"input"}
                        value={task.description}
                        onChange={onChange}
                        required
                        error={!!taskErrors.description}
                        helperText={taskErrors.description}
                    />

                    {
                        enabledFields && enabledFields.includes('action_category') &&
                        <FieldControl
                            keyName={"actionCategory"}
                            label={"Action Category"}
                            value={task.actionCategory}
                            variant={"select"}
                            options={getFilterOptions('MEETING_ACTION_CATEGORY').map((value) => {
                                return {
                                    label: value.display_text,
                                    value: value.key
                                }
                            })}
                            onChange={onChange}
                            required
                            error={!!taskErrors.actionCategory}
                            helperText={taskErrors.actionCategory}
                        />
                    }

                    {
                        enabledFields && enabledFields.includes('task_category') &&
                        <FieldControl
                            keyName={"actionCategory"}
                            label={"Task Category"}
                            value={task.actionCategory}
                            variant={"select"}
                            options={filterCommercialCategory({ domain: "TASK_CATEGORY", filterValue: "Commercial", isInclude: isCommercialPerson, isAdminPerson }).map((value) => {
                                return {
                                    label: value.display_text,
                                    value: value.key
                                }
                            })}
                            onChange={onChange}
                            required
                            error={!!taskErrors.actionCategory}
                            helperText={taskErrors.actionCategory}
                        />
                    }

                    {
                        disableFields && !disableFields.includes('tags') &&
                        <FieldControl
                            keyName={"tags"}
                            label={"Tags"}
                            value={task.tags}
                            variant={"select"}
                            options={getFilterOptions('TASK_TAG').map((value) => {
                                return {
                                    label: value.display_text,
                                    value: value.key
                                }
                            })}
                            onChange={onChange}
                            multiSelect

                        />
                    }

                    <FieldControl
                        keyName={"status"}
                        label={"Status"}
                        value={task.status}
                        variant={"select"}
                        options={getFilterOptions('TASK_STATUS').map((value) => {
                            return {
                                label: value.display_text,
                                value: value.key
                            }
                        })}
                        onChange={onChange}
                        required
                        error={!!taskErrors.status}
                        helperText={taskErrors.status}
                    />

                    <FieldControl
                        keyName={"priority"}
                        label={"Priority"}
                        variant={"select"}
                        value={task.priority}
                        options={getFilterOptions('TASK_PRIORITY').map((value) => {
                            return {
                                label: value.display_text,
                                value: value.key
                            }
                        })}
                        onChange={onChange}
                        required
                        error={!!taskErrors.priority}
                        helperText={taskErrors.priority}
                    />

                    <FieldControl
                        keyName={'assigned_to'}
                        label={"Assigned to"}
                        variant={"select"}
                        options={userOptions.map((u) => ({
                            value: u.id,
                            label: u.name,
                        }))}
                        loading={isLoadingUsers}
                        value={task.assigned_to}
                        onChange={onChange}
                        required
                        error={!!taskErrors.assigned_to}
                        helperText={taskErrors.assigned_to}
                    />

                    <FieldControl
                        keyName={"target_date"}
                        label={"Target Date"}
                        variant={"datetime"}
                        value={task.target_date}
                        onChange={onChange}
                        required
                        error={!!taskErrors.target_date}
                        helperText={taskErrors.target_date}
                    />

                    <DataLayout
                        title="Attachments"
                        children={
                            <Box p={2}>
                                <FileUpload type={"tasks"} typeId={taskId} ref={fileUploadRef} onUploadComplete={() => {
                                    removeCache();
                                    onClose();
                                    refetch();
                                }} />
                            </Box>
                        }
                    />

                </Box>
            </Box>
            <Stack direction="row" spacing={2} justifyContent="flex-end" flexShrink={0} bgcolor={'action.hover'} p={2}>
                <Button variant="outlined" loading={addCheckList.isPending || editCheckList.isPending} disabled={addCheckList.isPending || editCheckList.isPending} onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={onSave} loading={addCheckList.isPending || editCheckList.isPending} disabled={addCheckList.isPending || editCheckList.isPending}>Save</Button>
            </Stack>
        </Box>
    )

}